import type { APIState, Result } from '@api';
import { Failure, Success, useFMSAPI, useErrorMessage } from '@api';
import { projectAtom } from '@data/auth';
import { environmentAtom } from '@data/fms/environment/states';
import type { VehicleRemoteControlStatusAction } from '@data/fms/vehicle/types';
import { useNotification } from '@data/notification';
import isNullOrUndefined from '@utils/isNullOrUndefined';
import type { AxiosResponse, AxiosError } from 'axios';
import { useAtomCallback } from 'jotai/utils';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 遠隔操作可否変更API
 */
export const useUpdateRemoteControlStatusAPI = (): {
  state: APIState;
  postUpdateRemoteControlStatus: (
    vehicleId: string,
    action: VehicleRemoteControlStatusAction,
  ) => Promise<boolean>;
} => {
  const [state, setState] = useState<APIState>('none');
  const { notifyError, notifySuccess } = useNotification();
  const fmsAPI = useFMSAPI();
  const getErrorMessage = useErrorMessage();
  const { t } = useTranslation();

  const postUpdateRemoteControlStatusAPI = useCallback(
    async (
      projectId: string,
      environmentId: string,
      vehicleId: string,
      action: VehicleRemoteControlStatusAction,
    ): Promise<Result<boolean, AxiosResponse>> => {
      try {
        await fmsAPI.post(
          `/${projectId}/environments/${environmentId}/vehicles/${vehicleId}/update_remote_control_status`,
          {
            action,
          },
        );
        return new Success(true);
      } catch (error) {
        return new Failure((error as AxiosError).response as AxiosResponse);
      }
    },
    [fmsAPI],
  );

  const postUpdateRemoteControlStatus = useAtomCallback(
    useCallback(
      async (
        get,
        _,
        vehicleId: string,
        action: VehicleRemoteControlStatusAction,
      ) => {
        const project = get(projectAtom);
        const environment = get(environmentAtom);
        if (isNullOrUndefined(project) || isNullOrUndefined(environment)) {
          notifyError({
            message: t('api.fms.post_vehicle_update_remote_control_status', {
              action: t(`common.action.${action}`),
              status: 'failed',
            }),
          });
          setState('hasError');
          return false;
        }
        setState('loading');
        const res = await postUpdateRemoteControlStatusAPI(
          project.id,
          environment.environment_id,
          vehicleId,
          action,
        );
        if (isNullOrUndefined(res.value) || res.isFailure()) {
          notifyError({
            message: `${t('api.fms.post_vehicle_update_remote_control_status', {
              action: t(`common.action.${action}`),
              status: 'failed',
            })}: ${getErrorMessage(res)}`,
          });
          return false;
        }
        notifySuccess({
          message: t('api.fms.post_vehicle_update_remote_control_status', {
            action: t(`common.action.${action}`),
            status: 'successfully',
          }),
          testIdAttrs: {
            wrapperTestId: 'remote_control_snackbar_success-status-div',
            closeButtonTestId:
              'remote_control_snackbar_success-click-close_button',
          },
        });
        setState('hasValue');
        return res.value;
      },
      [
        notifyError,
        notifySuccess,
        postUpdateRemoteControlStatusAPI,
        t,
        getErrorMessage,
      ],
    ),
  );

  return {
    state,
    postUpdateRemoteControlStatus,
  };
};
